/**
 * Auth context provider.
 *
 * Mode selection is decided ONCE at module init from build-time env — see
 * `resolveAuthMode` in ./authMode.ts:
 * - `mock`     → mock path (default for dev + autonomous build).
 * - `firebase` → real Firebase path (production with creds).
 * - `fleet`    → DEV ONLY; the site sits behind the fleet OAuth2 Proxy and
 *                identity is restored from `GET /api/me` (see ./fleetAuth.ts).
 *
 * The real Firebase path is reached via dynamic `import('./firebaseAuth')` so
 * Vite tree-shakes the heavy firebase/* SDK out of the bundle in mock mode.
 * The mock path is imported statically: it is tiny, dependency-free, the
 * default branch (so it is always bundled anyway), and its session restore
 * must run synchronously during render (see useState initializer below).
 * The fleet path is static for the same reason: tiny and dependency-free.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { resolveAuthMode, type AuthMode } from './authMode';
import { fleetGetIdToken, fleetRestoreSession, fleetSignIn, fleetSignOut } from './fleetAuth';
import { mockGetIdToken, mockRestoreSession, mockSignInWithGoogle, mockSignOut } from './mockAuth';
import type { AuthErrorInfo, AuthSession, AuthUser, GetIdToken } from './types';

export function isMockAuthMode(): boolean {
  return resolveAuthMode() === 'mock';
}

interface AuthContextValue {
  user: AuthUser | null;
  idToken: string | null;
  /**
   * True while a session is still being restored on startup — the real
   * Firebase SDK replaying its persisted session, or fleet mode asking
   * `/api/me`. Guards against redirecting an already-signed-in user to
   * /login before restoration completes (comment #8). Always false in mock
   * mode, which restores its persisted session synchronously on mount.
   */
  authLoading: boolean;
  /**
   * Fleet mode only: why the session could not be restored (network down,
   * backend error, account not on the dev allowlist). Null when the user is
   * simply signed out, and always null in mock / Firebase mode.
   */
  authError: AuthErrorInfo | null;
  /** Fleet mode only: re-run the session restore after an `authError`. */
  retryAuth: () => void;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  getIdToken: GetIdToken;
  authMode: AuthMode;
  isMockMode: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const authMode = resolveAuthMode();
  const isMockMode = authMode === 'mock';
  // Mock mode restores its persisted fixture session synchronously on mount so
  // a refresh of a guarded page rehydrates `user` before the first render —
  // no flash through /login (comment #8). Real mode starts null and rehydrates
  // asynchronously via the Firebase SDK (effect below).
  const [session, setSession] = useState<AuthSession | null>(() =>
    isMockMode ? mockRestoreSession() : null,
  );
  // Real mode begins in a "restoring" state until Firebase replays any
  // persisted session (see effect below). Mock mode restores synchronously
  // above, so it starts ready — keeping the mock path deterministic.
  const [authLoading, setAuthLoading] = useState(!isMockMode);
  const [authError, setAuthError] = useState<AuthErrorInfo | null>(null);
  // Bumped by retryAuth to re-run the fleet restore effect.
  const [restoreAttempt, setRestoreAttempt] = useState(0);

  const signInWithGoogle = useCallback(async () => {
    if (authMode === 'fleet') {
      // The proxy runs the OAuth flow; this is a full-page navigation away.
      fleetSignIn();
    } else if (isMockMode) {
      setSession(await mockSignInWithGoogle());
    } else {
      const { firebaseSignInWithGoogle } = await import('./firebaseAuth');
      setSession(await firebaseSignInWithGoogle());
    }
  }, [authMode, isMockMode]);

  const signOut = useCallback(async () => {
    if (authMode === 'fleet') {
      // The session lives in the proxy's cookie, so only the proxy can end it.
      // `session` is left alone: clearing it would flash /login while the
      // browser is already navigating away.
      fleetSignOut();
      return;
    }
    if (isMockMode) {
      await mockSignOut();
    } else {
      const { firebaseSignOut } = await import('./firebaseAuth');
      await firebaseSignOut();
    }
    setSession(null);
  }, [authMode, isMockMode]);

  const getIdToken = useCallback<GetIdToken>(
    async (forceRefresh?: boolean) => {
      if (authMode === 'fleet') {
        // Non-credential placeholder; the proxy supplies the real bearer.
        return fleetGetIdToken();
      }
      if (isMockMode) {
        // mock path always returns the fixture token; forceRefresh is moot.
        return mockGetIdToken();
      }
      const { firebaseGetIdToken } = await import('./firebaseAuth');
      return firebaseGetIdToken(forceRefresh ?? false);
    },
    [authMode, isMockMode],
  );

  const retryAuth = useCallback(() => {
    setRestoreAttempt((n) => n + 1);
  }, []);

  // Fleet mode: the proxy cookie is the session, so ask the backend who we
  // are. Re-runs when retryAuth bumps `restoreAttempt`.
  useEffect(() => {
    if (authMode !== 'fleet') return;

    let active = true;
    setAuthLoading(true);
    setAuthError(null);
    void (async () => {
      const result = await fleetRestoreSession();
      if (!active) return;
      setSession(result.status === 'signed-in' ? result.session : null);
      setAuthError(result.status === 'error' ? result.error : null);
      setAuthLoading(false);
    })();

    return () => {
      active = false;
    };
  }, [authMode, restoreAttempt]);

  // Mock mode already restored synchronously in the useState initializer, so
  // there is nothing to do here.
  //
  // In real mode, the Firebase SDK persists the session and replays it
  // asynchronously after init. Subscribe to that restoration so a refresh of
  // an authenticated page rehydrates `user` instead of bouncing to /login
  // (comment #8). The first callback — restored user or null — clears the
  // restoring flag.
  useEffect(() => {
    if (authMode !== 'firebase') return;

    let active = true;
    let unsubscribe: (() => void) | null = null;

    void (async () => {
      const { firebaseOnAuthStateChanged } = await import('./firebaseAuth');
      const off = await firebaseOnAuthStateChanged((restored) => {
        if (!active) return;
        setSession(restored);
        setAuthLoading(false);
      });
      if (!active) {
        off();
        return;
      }
      unsubscribe = off;
    })();

    return () => {
      active = false;
      if (unsubscribe) unsubscribe();
    };
  }, [authMode]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: session?.user ?? null,
      idToken: session?.idToken ?? null,
      authLoading,
      authError,
      retryAuth,
      signInWithGoogle,
      signOut,
      getIdToken,
      authMode,
      isMockMode,
    }),
    [session, authLoading, authError, retryAuth, signInWithGoogle, signOut, getIdToken, authMode, isMockMode],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
